import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, InputNumber,
  DatePicker, Select, Switch, message, Popconfirm, Typography, Row, Col,
  Statistic, Tooltip
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, TagOutlined,
  GiftOutlined, PercentageOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import BackButton from '../../components/BackButton';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const CouponManagement = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const response = await api.get('/partners/coupons/');
      setCoupons(response.data.results || response.data || []);
    } catch (error) {
      message.error(t('partner.coupons.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingCoupon(null);
    form.resetFields();
    form.setFieldsValue({
      discount_type: 'PERCENTAGE',
      usage_limit: 100,
      usage_limit_per_user: 1,
      is_active: true,
    });
    setModalVisible(true);
  };

  const openEditModal = (coupon) => {
    setEditingCoupon(coupon);
    form.setFieldsValue({
      code: coupon.code,
      name: coupon.name,
      description: coupon.description,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      min_order_amount: coupon.min_order_amount,
      max_discount_amount: coupon.max_discount_amount,
      usage_limit: coupon.usage_limit,
      usage_limit_per_user: coupon.usage_limit_per_user,
      validity: [dayjs(coupon.valid_from), dayjs(coupon.valid_until)],
      is_active: coupon.is_active,
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        ...values,
        valid_from: values.validity[0].toISOString(),
        valid_until: values.validity[1].toISOString(),
      };
      delete data.validity;

      if (editingCoupon) {
        await api.patch(`/partners/coupons/${editingCoupon.id}/`, data);
        message.success(t('partner.coupons.updateSuccess'));
      } else {
        await api.post('/partners/coupons/', data);
        message.success(t('partner.coupons.createSuccess'));
      }
      setModalVisible(false);
      fetchCoupons();
    } catch (error) {
      if (error.response?.data) {
        const errors = error.response.data;
        if (errors.code) {
          message.error(errors.code[0] || errors.code);
        } else {
          message.error(t('partner.coupons.saveFailed'));
        }
      } else if (!error.errorFields) {
        message.error(t('partner.coupons.saveFailed'));
      }
    }
  };

  const handleDelete = async (couponId) => {
    try {
      await api.delete(`/partners/coupons/${couponId}/`);
      message.success(t('partner.coupons.deleteSuccess'));
      fetchCoupons();
    } catch (error) {
      message.error(t('partner.coupons.deleteFailed'));
    }
  };

  const getStatusTag = (coupon) => {
    const now = dayjs();
    const validFrom = dayjs(coupon.valid_from);
    const validUntil = dayjs(coupon.valid_until);

    if (!coupon.is_active) {
      return <Tag color="default" icon={<CloseCircleOutlined />}>{t('partner.coupons.inactive')}</Tag>;
    }
    if (now.isBefore(validFrom)) {
      return <Tag color="blue" icon={<ClockCircleOutlined />}>{t('partner.coupons.scheduled')}</Tag>;
    }
    if (now.isAfter(validUntil)) {
      return <Tag color="red" icon={<CloseCircleOutlined />}>{t('partner.coupons.expired')}</Tag>;
    }
    if (coupon.usage_limit && coupon.times_used >= coupon.usage_limit) {
      return <Tag color="orange" icon={<CloseCircleOutlined />}>{t('partner.coupons.usedUp')}</Tag>;
    }
    return <Tag color="green" icon={<CheckCircleOutlined />}>{t('partner.coupons.active')}</Tag>;
  };

  const columns = [
    {
      title: t('partner.coupons.code'),
      dataIndex: 'code',
      key: 'code',
      render: (code) => (
        <Text strong copyable style={{ fontFamily: 'monospace' }}>
          {code}
        </Text>
      ),
    },
    {
      title: t('partner.coupons.discount'),
      key: 'discount',
      render: (_, record) => (
        <Space>
          {record.discount_type === 'PERCENTAGE' ? (
            <Tag color="blue" icon={<PercentageOutlined />}>
              {record.discount_value}%
            </Tag>
          ) : (
            <Tag color="green" icon={<GiftOutlined />}>
              €{record.discount_value}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('partner.coupons.usage'),
      key: 'usage',
      render: (_, record) => (
        <span>
          {record.times_used} / {record.usage_limit || '∞'}
        </span>
      ),
    },
    {
      title: t('partner.coupons.validity'),
      key: 'validity',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(record.valid_from).format('DD/MM/YYYY')}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(record.valid_until).format('DD/MM/YYYY')}
          </Text>
        </Space>
      ),
    },
    {
      title: t('partner.coupons.status'),
      key: 'status',
      render: (_, record) => getStatusTag(record),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title={t('common.edit')}>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title={t('partner.coupons.deleteConfirm')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Tooltip title={t('common.delete')}>
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Calculate stats
  const activeCoupons = coupons.filter(c => c.is_valid).length;
  const totalUsage = coupons.reduce((acc, c) => acc + c.times_used, 0);

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/partner/dashboard" label={t('partner.backToDashboard')} />

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('partner.coupons.totalCoupons')}
              value={coupons.length}
              prefix={<TagOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('partner.coupons.activeCoupons')}
              value={activeCoupons}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('partner.coupons.totalUsage')}
              value={totalUsage}
              prefix={<GiftOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <Space>
            <TagOutlined />
            <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
              {t('partner.coupons.title')}
            </span>
          </Space>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            {t('partner.coupons.create')}
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={coupons}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingCoupon ? t('partner.coupons.editCoupon') : t('partner.coupons.createCoupon')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSubmit}
        okText={editingCoupon ? t('common.save') : t('common.create')}
        cancelText={t('common.cancel')}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="code"
                label={t('partner.coupons.codeLabel')}
                rules={[
                  { required: true, message: t('partner.coupons.codeRequired') },
                  { min: 3, message: t('partner.coupons.codeMinLength') },
                ]}
                extra={t('partner.coupons.codeHelp')}
              >
                <Input
                  placeholder="SUMMER20"
                  style={{ textTransform: 'uppercase' }}
                  disabled={!!editingCoupon}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label={t('partner.coupons.nameLabel')}
              >
                <Input placeholder={t('partner.coupons.namePlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label={t('partner.coupons.descriptionLabel')}
          >
            <Input.TextArea rows={2} placeholder={t('partner.coupons.descriptionPlaceholder')} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="discount_type"
                label={t('partner.coupons.discountType')}
                rules={[{ required: true }]}
              >
                <Select>
                  <Select.Option value="PERCENTAGE">{t('partner.coupons.percentage')}</Select.Option>
                  <Select.Option value="FIXED">{t('partner.coupons.fixedAmount')}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="discount_value"
                label={t('partner.coupons.discountValue')}
                rules={[{ required: true, message: t('partner.coupons.discountRequired') }]}
              >
                <InputNumber min={0.01} step={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="max_discount_amount"
                label={t('partner.coupons.maxDiscount')}
                extra={t('partner.coupons.maxDiscountHelp')}
              >
                <InputNumber min={0} step={1} style={{ width: '100%' }} addonAfter="€" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="min_order_amount"
                label={t('partner.coupons.minOrder')}
              >
                <InputNumber min={0} step={1} style={{ width: '100%' }} addonAfter="€" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="usage_limit"
                label={t('partner.coupons.usageLimit')}
                rules={[{ required: true }]}
              >
                <InputNumber min={1} step={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="usage_limit_per_user"
                label={t('partner.coupons.perUserLimit')}
              >
                <InputNumber min={1} step={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="validity"
            label={t('partner.coupons.validityPeriod')}
            rules={[{ required: true, message: t('partner.coupons.validityRequired') }]}
          >
            <RangePicker
              showTime
              format="DD/MM/YYYY HH:mm"
              style={{ width: '100%' }}
              disabledDate={(current) => current && current < dayjs().startOf('day')}
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label={t('partner.coupons.activeLabel')}
            valuePropName="checked"
          >
            <Switch checkedChildren={t('common.active')} unCheckedChildren={t('common.inactive')} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CouponManagement;
